import { useState, useEffect } from "react";
import { fetchGetLaporanPengaduanAll } from "../../../../controller/dashboard/Pengaduan";
import { 
    User, 
    Calendar, 
    Eye,
    Clock,
    AlertTriangle,
    CheckCircle,
    XCircle,
    MessageSquare,
    Camera,
    Star
} from "lucide-react";
import { Pengaduan } from "../../../../model/Hublang";


export const Main = () => {
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(9); // bisa default 

    const [showDetailModal, setShowDetailModal] = useState(false);

    const [statusLength, setStatusLength] = useState('9');
    const [laporanData, setLaporanData] = useState<Pengaduan[]>([]);

    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [selectedComplaint, setSelectedComplaint] = useState<Pengaduan | null>(null);

    const filteredLaporan = laporanData.filter(plg => {
        const matchesSearch = true;
        const matchesStatus = true;
        return matchesSearch && matchesStatus;
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentLaporan = filteredLaporan.slice(indexOfFirstItem, indexOfLastItem);
    let totalPages = itemsPerPage > 0 ? Math.ceil(filteredLaporan.length / itemsPerPage) : 0;
    const pageNumbers = totalPages > 0 ? Array.from({ length: totalPages }, (_, i) => i + 1) : [];

    const initDataPengaduan = async () => { 
        setLoading(true); // Set loading state ke true sebelum memulai fetch
        try {
            const res = await fetchGetLaporanPengaduanAll(); // Panggil API
            if (res.status === 200 && Array.isArray(res.data)) {

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const processedData = res.data.map((item: any) => ({
                    id                  : item.id,
                    no_pelanggan        : item.no_pelanggan,
                    nama                : item.nama,
                    image               : item.gambar_eksekusi != '' ? [ item.gambar, item.gambar_eksekusi ] : [ item.gambar ],
                    no_telp             : item.no_telp,
                    deskripsi           : item.deskripsi_eksekusi != '' ? [ item.deskripsi, item.deskripsi_eksekusi ] : [ item.deskripsi ],
                    lokasi              : item.lokasi,
                    alamat              : item.alamat,
                    tanggal             : item.tanggal,
                    status              : item.status,
                    rating              : item.rating,
                    responden           : item.responden,
                    eksekutor           : item.eksekutor,
                    laporan_path        : item.laporan_path,
                    berita_acara_path   : item.berita_acara_path,
                    authorized          : item.authorized,
                    modified_at         : item.modified_at,
                }));

                const sortedData = processedData;
                setLaporanData(sortedData);
            } else {
                console.error("Invalid response data:", res.data);
                setLaporanData([]);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setLaporanData([]);
        } finally {
            setLoading(false);
        }
    };

    const RenderTablePetugasMeter = ({ filteredData }: { 
        filteredData: Pengaduan[] | null; 
    }) => (
        <>
        {
            filteredData && filteredData.length > 0 ? (
                filteredData.map((plg, idx) => (
                    <tr key={idx} className="bg-gray-50 hover:bg-gray-200 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                            {idx + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {plg.nama}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            {plg.alamat}
                        </td>
                    </tr>
                ))
            ) : (
            <tr>
                <td colSpan={18} className="text-center py-4 text-gray-500">
                Tidak ada data
                </td>
            </tr>
            )
        }
        </>
    );

    const filteredComplaints = laporanData.filter(complaint => {
        const matchesSearch = 
            complaint.no_pelanggan.toLowerCase().includes(searchTerm.toLowerCase()) ||
            complaint.nama.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || complaint.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    // const filteredComplaints = false;
    
    const renderStars = (rating: number) => {
        return (
          <div className="flex items-center space-x-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-4 h-4 ${
                  star <= rating
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'fill-gray-200 text-gray-200'
                }`}
              />
            ))}
          </div>
        );
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
        case 'respon':
            return (
                <span className="px-3 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full flex items-center w-fit">
                    <Clock className="w-3 h-3 mr-1" />
                    Menunggu
                </span>
            );
        case 'eksekusi':
            return (
                <span className="px-3 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full flex items-center w-fit">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Diproses
                </span>
            );
        case 'selesai':
            return (
                <span className="px-3 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full flex items-center w-fit">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Selesai
                </span>
            );
        case 'belum_selesai':
            return (
                <span className="px-3 py-1 text-xs font-semibold bg-grey-100 text-grey-800 rounded-full flex items-center w-fit">
                    <XCircle className="w-3 h-3 mr-1" />
                    Ditolak
                </span>
            );
        default:
            return (
                <span className="px-3 py-1 text-xs font-semibold bg-red-100 text-red-800 rounded-full flex items-center w-fit">
                    <Clock className="w-3 h-3 mr-1" />
                    Belum Direspon
                </span>
            )
        }
    };
    
    const getPriorityBadge = (priority: string) => {
        switch (priority) {
            case 'high':
            return (
                <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800 rounded">
                Tinggi
                </span>
            );
            case 'medium':
            return (
                <span className="px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-800 rounded">
                Sedang
                </span>
            );
            case 'low':
            return (
                <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded">
                Rendah
                </span>
            );
            default:
            return null;
        }
    };

    const handleDetailModal = (state : boolean) => {
        if(state) setShowDetailModal(true);
        else setShowDetailModal(false);
    }

    const DetailModal = ({ complaint, stateMethod }: { complaint: Pengaduan; stateMethod : (state : any) => void }) => {
        const images = complaint?.image.length > 1 ? [ complaint?.image[0], complaint?.image[1] ] : [ complaint?.image[0] ];
        
        return(<>          
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
                        <h3 className="text-xl font-semibold text-gray-800">Detail Pengaduan</h3>
                            <button onClick={() => stateMethod(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
                    </div>
                
                    <div className="p-6 space-y-6">
                        {/* Header Info */}
                        <div className="flex items-start justify-between">
                            <div>
                                <h4 className="text-2xl font-bold text-gray-900 mb-2">{complaint?.no_pelanggan} - {complaint?.nama}</h4>
                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                                    <div className="flex items-center">
                                        <User className="w-4 h-4 mr-1" />
                                        {complaint?.nama}
                                    </div>
                                    <div className="flex items-center">
                                        <Calendar className="w-4 h-4 mr-1" />
                                        {complaint.tanggal ?? ''}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col items-end space-y-2">
                                {getStatusBadge(complaint?.status ?? '')}
                                {/* {getPriorityBadge(complaint?.priority ?? '')} */}
                            </div>
                        </div>
            
                        {/* Category */}
                        {/* <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-sm text-gray-600 mb-1">Kategori</div>
                            <div className="font-semibold text-gray-900">{complaint?.category}</div>
                        </div> */}
            
                        {/* Description */}
                        <div>
                            <div className="text-sm font-semibold text-gray-700 mb-2">Deskripsi Pengaduan</div>
                            <p className="text-gray-700 leading-relaxed">{complaint?.deskripsi}</p>
                        </div>

                        {/* Rating Section */}
                        { complaint?.rating ? (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                <div className="text-sm font-semibold text-gray-700 mb-2">Penilaian Penanganan</div>
                                <div className="flex items-center space-x-3">
                                    {renderStars(parseInt(complaint?.rating))}
                                    <span className="text-2xl font-bold text-gray-800">
                                    {complaint?.rating}.0
                                    </span>
                                    <span className="text-sm text-gray-600">dari 5</span>
                                </div>
                                </div>
                            </div>
                            </div>
                         )
                         :
                         (
                            <></>
                         )
                        }

                        {/* Images */}
                        <div>
                            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center justify-between">
                                <span>Foto Bukti</span>
                                <span className="flex items-center space-x-1 bg-gray-100 rounded-full px-3 py-1">
                                    <Camera className="w-3 h-3 text-gray-600" />
                                    <span className="text-xs font-semibold text-gray-700">{images.length} Foto</span>
                                </span>
                            </div>
                            <div className={`grid gap-4 ${images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                                {images.map((image, index) => (
                                    <div key={index} className="relative group">
                                    <img 
                                        src={`${process.env.REACT_APP_API_URL}/storage/${image}`} 
                                        alt={`Bukti ${index + 1}`}
                                        className={`w-full object-cover rounded-lg shadow-md ${images.length === 1 ? 'h-96' : 'h-64'}`}
                                    />
                                    {/* Image number badge */}
                                    <div className={`absolute bottom-2 left-2 bg-opacity-90 p-1 rounded-sm flex items-center justify-center`} style={{ backgroundColor : index === 0 ? 'blue' : 'green'}}>
                                        <span className={`text-xs font-bold text-700`} style={{ color: 'white' }}>{index === 0 ? 'Laporan' : 'Eksekusi'}</span>
                                    </div>
                                    {/* Hover overlay */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                                        <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                    </div>
                                ))}
                            </div>
                        </div>
            
                        {/* Actions */}
                        {/* <div className="flex space-x-3 pt-4 border-t">
                            <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Tandai Diproses</button>
                            <button className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">Tandai Selesai</button>
                            <button className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">Tolak</button>
                        </div> */}
                    </div>
                </div>
            </div>  
        </>)
    };
    
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };
    
    useEffect(() => {
        initDataPengaduan();
        setItemsPerPage(Number(statusLength === 'all' ? laporanData.length : statusLength))
    }, []);

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentLaporan.map((complaint, idx) => (
                    <div 
                    key={complaint.id} 
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] cursor-pointer"
                    onClick={() => {
                        console.log("CLICK  in : " + (idx + 1));
                        setSelectedComplaint(complaint);
                        handleDetailModal(true);
                    }}
                    >
                        {/* Images Preview */}
                        <div className="relative h-48 bg-gray-200">
                            <div className={`grid h-full ${complaint.image.length === 1 ? 'grid-cols-1' : 'grid-cols-2 gap-0.5'}`}>
                                {complaint.image.slice(0, 2).map((image, index) => (
                                    <div key={index} className="relative overflow-hidden bg-gray-100">
                                    <img 
                                        src={`${process.env.REACT_APP_API_URL}/storage/${image}`} 
                                        alt={`Preview ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                    {/* Image number badge - only show if more than 1 image */}
                                    {/* { complaint.images.length > 1 && ( */}
                                        <div className={`absolute bottom-2 left-2 bg-opacity-90 p-1 rounded-sm flex items-center justify-center`} 
                                            style={{ backgroundColor : index === 0 ? 'blue' : 'green'}}
                                        >
                                            <span className={`text-xs font-bold text-700`} style={{ color: 'white' }}>{index === 0 ? 'Laporan' : 'Eksekusi'}</span>
                                        </div>
                                    {/* )} */}
                                    </div>
                                ))}
                            </div>

                            {/* Vertical divider between images - only show if 2 images */}
                            {complaint.image.length > 1 && (
                                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 h-full w-1 bg-white"></div>
                            )}
                            
                            {/* Image count badge */}
                            <div className="absolute top-2 right-2 flex items-center space-x-1 bg-black bg-opacity-70 rounded-full px-3 py-1">
                                <Camera className="w-3 h-3 text-white" />
                                <span className="text-xs text-white font-semibold">{complaint.image.length}</span>
                            </div>
                        </div>

                        {/* Card Content */}
                        <div className="p-5">
                            {/* Header */}
                            {/* <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 text-lg mb-1 line-clamp-2">
                                    {complaint.title}
                                    </h3>
                                    <div className="flex items-center text-sm text-gray-600 mb-2">
                                    <User className="w-3 h-3 mr-1" />
                                    {complaint.employeeName}
                                    </div>
                                </div>
                                {getPriorityBadge(complaint.priority)}
                            </div> */}

                            {/* Description */}
                            <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                                {complaint.deskripsi[0]}
                            </p>

                            <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                                {complaint.alamat}
                            </p>

                            {/* Rating - only show if exists */}
                            { complaint.rating ? (
                                <div className="mb-4 flex items-center space-x-2">
                                    {renderStars(parseInt(complaint?.rating))}
                                    <span className="text-sm font-semibold text-gray-700">
                                    {complaint.rating}.0
                                    </span>
                                </div>
                             )
                             :
                             (
                                <></>
                             )
                            }

                            {/* Footer */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                <div className="flex items-center text-xs text-gray-500">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    {complaint.tanggal}
                                </div>
                            {getStatusBadge(complaint.status)}
                            </div>

                            {/* Category Tag */}
                            {/* <div className="mt-3">
                                <span className="inline-block px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded">
                                    {complaint.category}
                                </span>
                            </div> */}
                        </div>
                    </div>
                ))}
            </div>

            {/* Kosong */}
            {filteredComplaints.length === 0 && (
                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                    <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Tidak ada pengaduan ditemukan
                    </h3>
                    <p className="text-gray-500">
                    Coba ubah filter atau kata kunci pencarian Anda
                    </p>
                </div>
            )}
    
            {/* Pagination */}
            <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                    Menampilkan <span className="font-medium">{currentPage}</span> hingga{' '}
                    <span className="font-medium">{statusLength === "all" ? laporanData.length : statusLength}</span> dari{' '}
                    <span className="font-medium">{laporanData.length}</span> karyawan
                    <span className="font-medium">( Dari Sortir {currentLaporan.length} )</span>
                </div>
                <div className="flex space-x-2">
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)} className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                        First
                    </button>
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)} className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                        Previous
                    </button>
                    { pageNumbers.slice(Math.max(0, currentPage - 3), Math.min(currentPage + 2, totalPages)).map((page) =>( 
                        <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 rounded ${ currentPage === page ? "bg-blue-500 text-white" : "bg-gray-200" }`}> {page} </button>
                    ))}
                    <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)} className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"> Next </button>
                    <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)} className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"> Last </button>
                </div>
            </div>
            
            { showDetailModal && selectedComplaint && <DetailModal complaint={selectedComplaint} stateMethod={handleDetailModal} /> }
        </>
    )
}